/**
 * Shared API client configuration and utilities
 */

import { config } from "@/config";
import { getErrorMessage } from "@/services/utils";

export const BASE_URL = config.apiBaseUrl;

export const FETCH_DEFAULT_OPTIONS: RequestInit = {
  headers: {
    "Content-Type": "application/json",
    "X-SKH-Content-Type": "front",
  },
  credentials: "include",
};

export function checkResponseStatus(r: Response, expectedStatusCode: number) {
  if (r.status !== expectedStatusCode) {
    throw new Error(`Server responded with unexpected status code: ${r.status}`);
  }
}

export interface FastApiValidationError {
  loc: Array<string | number>;
  msg: string;
  type: string;
}

/** JSON body returned by FastAPI: a message, a `detail` payload, or a validation error list. */
export type FastApiErrorResponse =
  | { message: string }
  | { detail: string | FastApiValidationError[] }
  | FastApiValidationError[];

export interface ApiErrorResponse {
  status: number;
  error: FastApiErrorResponse | null;
}

function isFastApiValidationError(value: unknown): value is FastApiValidationError {
  if (!value || typeof value !== "object") {
    return false;
  }
  const item = value as Record<string, unknown>;
  return Array.isArray(item.loc) && typeof item.msg === "string" && typeof item.type === "string";
}

function isFastApiValidationErrorList(value: unknown): value is FastApiValidationError[] {
  return Array.isArray(value) && value.every(isFastApiValidationError);
}

function validationErrorText(errors: FastApiValidationError[]) {
  return errors.map((item) => item.msg).join("\n");
}

export function fastApiErrorMessage(error: FastApiErrorResponse) {
  if (Array.isArray(error)) {
    return validationErrorText(error);
  }
  if ("message" in error) {
    return error.message;
  }
  if (typeof error.detail === "string") {
    return error.detail;
  }
  return validationErrorText(error.detail);
}

function toFastApiErrorResponse(body: unknown): FastApiErrorResponse | null {
  if (isFastApiValidationErrorList(body)) {
    return body;
  }
  if (!body || typeof body !== "object") {
    return null;
  }
  const record = body as Record<string, unknown>;
  if (typeof record.message === "string") {
    return { message: record.message };
  }
  if (typeof record.detail === "string" || isFastApiValidationErrorList(record.detail)) {
    return { detail: record.detail };
  }
  return null;
}

async function readApiErrorResponse(response: Response): Promise<ApiErrorResponse> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  return { status: response.status, error: toFastApiErrorResponse(body) };
}

/**
 * Options for API request methods
 */
export interface ApiRequestOptions<T> {
  /** Expected HTTP status code (default: 200) */
  expectedStatus?: number;
  /** Default value to return on error */
  defaultValue?: T;
  /**
   * When true, an unexpected HTTP status returns `ApiErrorResponse` instead of `defaultValue`.
   * The caller interprets the FastAPI body.
   */
  returnErrorResponse?: boolean;
  /** Custom error handler (if not provided, logs to console) */
  onError?: (error: unknown, statusCode?: number, apiError?: ApiErrorResponse) => void;
  /** Whether to parse JSON response (default: true) */
  parseJson?: boolean;
  /** AbortSignal to cancel the request (e.g. from AbortController) */
  signal?: AbortSignal;
}

/**
 * Generic API request handler
 */
type ApiRequestReturn<T, D> = D extends undefined ? T | undefined : T | D;

async function apiRequest<T, D = undefined>(
  url: string,
  method: string,
  options: ApiRequestOptions<T> & { defaultValue?: D } = {},
  body?: unknown
): Promise<ApiRequestReturn<T, D>> {
  const { expectedStatus = 200, defaultValue, onError, parseJson = true } = options;

  let response: Response | undefined;

  try {
    const requestOptions: RequestInit = {
      method,
      ...FETCH_DEFAULT_OPTIONS,
      ...(options.signal && { signal: options.signal }),
    };

    if (body !== undefined) {
      requestOptions.body = typeof body === "string" ? body : JSON.stringify(body);
    }

    response = await fetch(`${BASE_URL}${url}`, requestOptions);
    checkResponseStatus(response, expectedStatus);

    if (parseJson && expectedStatus !== 204) {
      return (await response.json()) as ApiRequestReturn<T, D>;
    }

    return defaultValue as ApiRequestReturn<T, D>;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }
    const apiError =
      response && response.status !== expectedStatus
        ? await readApiErrorResponse(response)
        : undefined;
    if (onError) {
      onError(error, response?.status, apiError);
    } else {
      console.error(getErrorMessage(error));
    }
    if (options.returnErrorResponse && apiError) {
      return apiError as ApiRequestReturn<T, D>;
    }
    return defaultValue as ApiRequestReturn<T, D>;
  }
}

/**
 * HTTP GET request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiGet<T>(
  url: string,
  options: ApiRequestOptions<T> & { returnErrorResponse: true; defaultValue?: undefined }
): Promise<T | ApiErrorResponse | undefined>;
export async function apiGet<T>(
  url: string,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiGet<T>(
  url: string,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiGet<T>(
  url: string,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    // When defaultValue is provided, it must be of type T, so D = T
    return apiRequest<T, T>(url, "GET", options as ApiRequestOptions<T> & { defaultValue: T });
  }
  return apiRequest<T, undefined>(
    url,
    "GET",
    options as ApiRequestOptions<T> & { defaultValue?: undefined }
  );
}

/**
 * HTTP POST request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiPost<T>(
  url: string,
  body: unknown | undefined,
  options: ApiRequestOptions<T> & { returnErrorResponse: true; defaultValue?: undefined }
): Promise<T | ApiErrorResponse | undefined>;
export async function apiPost<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiPost<T>(
  url: string,
  body: unknown | undefined,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiPost<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    return apiRequest<T, T>(
      url,
      "POST",
      { expectedStatus: 201, ...options } as ApiRequestOptions<T> & { defaultValue: T },
      body
    );
  }
  return apiRequest<T, undefined>(
    url,
    "POST",
    { expectedStatus: 201, ...options } as ApiRequestOptions<T> & { defaultValue?: undefined },
    body
  );
}

/**
 * HTTP PUT request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiPut<T>(
  url: string,
  body: unknown | undefined,
  options: ApiRequestOptions<T> & { returnErrorResponse: true; defaultValue?: undefined }
): Promise<T | ApiErrorResponse | undefined>;
export async function apiPut<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiPut<T>(
  url: string,
  body: unknown | undefined,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiPut<T>(
  url: string,
  body?: unknown,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    return apiRequest<T, T>(
      url,
      "PUT",
      options as ApiRequestOptions<T> & { defaultValue: T },
      body
    );
  }
  return apiRequest<T, undefined>(
    url,
    "PUT",
    options as ApiRequestOptions<T> & { defaultValue?: undefined },
    body
  );
}

/**
 * HTTP DELETE request
 *
 * Overloads:
 * - Without defaultValue: Returns Promise<T | undefined> (may return undefined on error)
 * - With defaultValue: Returns Promise<T> (always returns a value, never undefined)
 */
export async function apiDelete<T>(
  url: string,
  options: ApiRequestOptions<T> & { returnErrorResponse: true; defaultValue?: undefined }
): Promise<T | ApiErrorResponse | undefined>;
export async function apiDelete<T>(
  url: string,
  options?: ApiRequestOptions<T> & { defaultValue?: undefined }
): Promise<T | undefined>;
export async function apiDelete<T>(
  url: string,
  options: ApiRequestOptions<T> & { defaultValue: T }
): Promise<T>;
export async function apiDelete<T>(
  url: string,
  options?: ApiRequestOptions<T>
): Promise<T | undefined> {
  if (options?.defaultValue !== undefined) {
    return apiRequest<T, T>(url, "DELETE", {
      expectedStatus: 204,
      ...options,
    } as ApiRequestOptions<T> & { defaultValue: T });
  }
  return apiRequest<T, undefined>(url, "DELETE", {
    expectedStatus: 204,
    ...options,
  } as ApiRequestOptions<T> & { defaultValue?: undefined });
}
